import { Alert, Modal, Spin } from 'antd';
import Button from 'components/common/Button';
import { useCreateInput } from 'hooks/inputs/useCreateInput';
import { useUpsertInputTask } from 'hooks/inputs/useUpsertInputTask';
import { useCropCatalogue } from 'hooks/useCropCatalogue';
import { useFetchGAreaUnits } from 'hooks/useFetchGAreaUnits';
import { useFetchZoneById } from 'hooks/useFetchZoneById';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { useMOrgId } from 'hooks/useMOrgId';
import { useProductionTonneUnit } from 'hooks/useProductionTonneUnit';
import { GcVariety, GUnit, MergedCrop } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  buildCropPatch,
  buildUnitPatch,
  buildVarietyPatch,
  buildZoneAreaPatch,
  isInputDraftValid,
  resolveAreaUnit,
  resolveUnitValue,
  toCalendarDateIso,
} from 'utils/inputDraftPatches';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import { CREATE_PLAN_ERROR_MESSAGE } from 'constants/messageConstants';
import InputAttributesForm from 'pages/FarmsPage/Workspace/InputAttributesForm';
import { buildDefaultPlanTasks } from 'pages/FarmsPage/Workspace/Tasks/planCropDefaults';
import { buildBlankInput } from './buildBlankInput';

// Stable default so a loading/disabled units query does not mint a new array
// on every render and invalidate the memos that depend on it.
const EMPTY_G_UNITS: GUnit[] = [];

interface CreatePlanModalProps {
  open: boolean;
  paddock: IPaddock | null;
  activeOrganisationId: string | null;
  gOrgId: string | null;
  onClose: () => void;
  onCreated: (createdInput: IInput) => void;
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  open,
  paddock,
  activeOrganisationId,
  gOrgId,
  onClose,
  onCreated,
}) => {
  const [draft, setDraft] = useState<IInput | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<MergedCrop | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  // Guards against re-seeding the draft (and wiping what the user typed) when
  // an unrelated dependency such as the user profile resolves.
  const seededPaddockIdRef = useRef<string | null>(null);

  const { data: userProfile } = useFetchUserProfile();
  const mOrgId = useMOrgId();
  const { data: gUnits = EMPTY_G_UNITS } = useFetchGAreaUnits(gOrgId);

  const createMutation = useCreateInput({ activeOrganisationId });
  const { mutateAsync: upsertTask } = useUpsertInputTask({
    activeOrganisationId,
  });
  const { tonneUnit } = useProductionTonneUnit(gOrgId);
  // Covers the default production task's save too, so Create can't be
  // pressed twice while it's in flight.
  const [isCreating, setIsCreating] = useState(false);
  const isPending = createMutation.isPending || isCreating;

  const {
    items: browseCrops,
    allMergedItems,
    searchItems,
    isLoading: isCropsLoading,
    isPaginating: isCropsPaginating,
    hasNextPage: hasNextCropPage,
    fetchNextPage,
    searchTerm: cropSearchTerm,
    onSearch: onCropSearch,
    clearSearch: clearCropSearch,
  } = useCropCatalogue(gOrgId, mOrgId, draft?.crop?.globalId ?? null);

  // The Input's zone is a projection of the Zone document, so the plan cannot
  // be seeded until the zone has loaded — reconstructing it from the paddock
  // is what previously produced the wrong zoneName and a null geoData.
  const hasZoneId = !!paddock?.zoneId;
  const {
    data: zone,
    isLoading: isZoneLoading,
    isError: isZoneError,
  } = useFetchZoneById(paddock?.zoneId);

  // A disabled query reports neither loading nor error, so a paddock with no
  // zoneId has to be treated as unavailable explicitly — otherwise the modal
  // would render blank with no spinner, no error and Create disabled forever.
  const isZoneUnavailable = !!paddock && (!hasZoneId || isZoneError);
  const zoneUnavailableMessage = !hasZoneId
    ? 'This paddock has no zone linked, so a plan cannot be created for it.'
    : "Unable to load this paddock's zone. A plan cannot be created until it loads — please close and try again.";

  useEffect(() => {
    if (!open || !paddock || !activeOrganisationId || !zone) {
      if (!open || !paddock) seededPaddockIdRef.current = null;
      return;
    }

    if (seededPaddockIdRef.current === paddock.paddockId) return;
    seededPaddockIdRef.current = paddock.paddockId;

    setDraft(
      buildBlankInput({
        paddock,
        zone,
        gUnits,
        orgId: activeOrganisationId,
        userProfile,
      })
    );
    setSelectedCrop(null);
    setApiError(null);
    clearCropSearch();
  }, [
    open,
    paddock,
    zone,
    gUnits,
    activeOrganisationId,
    userProfile,
    clearCropSearch,
  ]);

  const updateDraft = useCallback((patch: Partial<IInput>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Keeps a search-selected crop visible in the dropdown even when the browse
  // list has not paginated far enough to include it yet.
  const cropOptions = useMemo(() => {
    if (
      selectedCrop &&
      !browseCrops.some((crop) => crop.globalId === selectedCrop.globalId)
    ) {
      return [selectedCrop, ...browseCrops];
    }
    return browseCrops;
  }, [browseCrops, selectedCrop]);

  const varietyOptions = useMemo(
    () => selectedCrop?.varieties ?? [],
    [selectedCrop]
  );

  const unitValue = useMemo(
    () => resolveUnitValue(draft?.zone, gUnits),
    [draft?.zone, gUnits]
  );

  const handleCropChange = useCallback(
    (value: string) => {
      const option =
        allMergedItems.find((crop) => crop.globalId === value) ??
        searchItems.find((crop) => crop.globalId === value);
      if (!option) return;

      setSelectedCrop(option);
      clearCropSearch();
      updateDraft(buildCropPatch(option));
    },
    [allMergedItems, searchItems, clearCropSearch, updateDraft]
  );

  const handleVarietyChange = useCallback(
    (value: string | undefined) => {
      const option = varietyOptions.find(
        (variety) => variety.varietyId === value
      );
      updateDraft(buildVarietyPatch(draft, option));
    },
    [draft, varietyOptions, updateDraft]
  );

  // A crop just added from the crop field (OP-2498), selected straight away.
  const handleCropAdded = useCallback(
    (crop: MergedCrop) => {
      setSelectedCrop(crop);
      clearCropSearch();
      updateDraft(buildCropPatch(crop));
    },
    [clearCropSearch, updateDraft]
  );

  // A variety just added to the selected crop: listed on it, and selected.
  const handleVarietyAdded = useCallback(
    (variety: GcVariety) => {
      setSelectedCrop((crop) =>
        crop
          ? {
              ...crop,
              varieties: [
                ...crop.varieties.filter(
                  (existing) => existing.varietyId !== variety.varietyId
                ),
                variety,
              ],
            }
          : crop
      );
      updateDraft(buildVarietyPatch(draft, variety));
    },
    [draft, updateDraft]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      updateDraft({ startDate: toCalendarDateIso(date) });
    },
    [updateDraft]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      updateDraft({ endDate: toCalendarDateIso(date) });
    },
    [updateDraft]
  );

  const handleZoneAreaChange = useCallback(
    (value: number | null) => {
      updateDraft(buildZoneAreaPatch(draft?.zone, value));
    },
    [draft?.zone, updateDraft]
  );

  const handleUnitChange = useCallback(
    (value: string) => {
      const option = gUnits.find((unit) => unit.globalId === value);
      if (!option) return;
      updateDraft(buildUnitPatch(draft?.zone, option));
    },
    [draft?.zone, gUnits, updateDraft]
  );

  const fetchNextCropPage = useCallback(() => {
    if (!isCropsPaginating && hasNextCropPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextCropPage, isCropsPaginating]);

  // The zone must be loaded before a plan can be written, so a missing or
  // failed zone blocks creation rather than silently falling back to
  // paddock-derived values.
  const canCreate = isInputDraftValid(draft) && !!zone && !isZoneError;

  const handleCreate = useCallback(() => {
    if (!draft || !canCreate || isPending) return;

    setApiError(null);
    // A paddock's area unit can carry an empty globalId. Resolve it from the
    // unit catalogue here rather than at seed time, so the lookup cannot race
    // the units query still loading when the modal opened.
    const payload: IInput = draft.zone
      ? {
          ...draft,
          zone: {
            ...draft.zone,
            areaUnit: resolveAreaUnit(draft.zone.areaUnit, gUnits),
          },
        }
      : draft;

    setIsCreating(true);
    createMutation.mutate(payload, {
      onSuccess: async (response) => {
        const created = response?.data?.[0] ?? payload;
        // A new plan starts with crop establishment and production tasks in
        // its crop (OP-2536), saved one after the other before the plan opens
        // so Planning shows them straight away. The plan exists either way,
        // so a failed task save doesn't stop it.
        const defaultTasks = buildDefaultPlanTasks(
          created,
          {
            crop: created.crop,
            cropOption: selectedCrop,
            tonneUnit,
            isResolving: false,
          },
          userProfile
        );
        let opened = created;
        for (const task of defaultTasks) {
          try {
            const taskResponse = await upsertTask({
              inputId: created.id,
              task,
            });
            opened = taskResponse?.data?.[0] ?? {
              ...opened,
              tasks: [...(opened.tasks ?? []), task],
            };
          } catch {
            // The plan opens without it; the task can be added by hand.
          }
        }
        setIsCreating(false);
        onCreated(opened);
        onClose();
      },
      onError: (error) => {
        setIsCreating(false);
        setApiError(
          getApiErrorMessage(
            error,
            CREATE_PLAN_ERROR_MESSAGE
          )
        );
      },
    });
  }, [
    createMutation,
    draft,
    gUnits,
    isPending,
    canCreate,
    onClose,
    onCreated,
    selectedCrop,
    tonneUnit,
    upsertTask,
    userProfile,
  ]);

  const footer = (
    <div className="flex justify-end gap-3 pr-2">
      <Button
        variant="secondary"
        className="px-4 py-1 text-base font-semibold"
        onClick={onClose}
        disabled={isPending}
        type="button"
      >
        Cancel
      </Button>
      <Button
        variant="success"
        className="px-4 py-1 text-base font-semibold disabled:opacity-50"
        onClick={handleCreate}
        disabled={!canCreate || isPending}
        type="button"
      >
        {isPending ? 'Creating...' : 'Create'}
      </Button>
    </div>
  );

  return (
    <Modal
      title={
        <span className="ml-1 text-lg font-semibold text-gray-dark">
          New Paddock Plan
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      mask={{ closable: !isPending }}
      keyboard={!isPending}
      closable={!isPending}
      width={680}
      className="!max-w-[calc(100vw-2rem)]"
      classNames={{
        root: '!rounded-2xl',
        header: '!mb-0',
        body: '!max-h-[80vh] !overflow-y-auto !overflow-x-hidden !p-1 !pr-2',
      }}
      destroyOnHidden={false}
    >
      {paddock && (
        <p className="mb-2 text-form-hint font-normal text-secondary-dark">
          Adding a plan to {paddock.paddockName}. An asterisk (*) indicates
          required fields.
        </p>
      )}

      {isZoneUnavailable && (
        <Alert message={zoneUnavailableMessage} type="error" className="mb-4" />
      )}

      {apiError && (
        <Alert
          message={apiError}
          type="error"
          className="mb-4"
          closable
          onClose={() => setApiError(null)}
        />
      )}

      {hasZoneId && isZoneLoading && !draft && (
        <div className="flex items-center justify-center py-8">
          <Spin size="small" />
          <span className="ml-2 text-form-hint text-secondary-dark">
            Loading paddock zone...
          </span>
        </div>
      )}

      {draft && (
        <InputAttributesForm
          className="max-w-full border-b-0 pb-0"
          draft={draft}
          selectedCropOption={selectedCrop ?? undefined}
          varietyOptions={varietyOptions}
          unitValue={unitValue}
          mergedCrops={cropOptions}
          gUnits={gUnits}
          isCropsLoading={isCropsLoading}
          isCropsPaginating={isCropsPaginating}
          hasNextCropPage={hasNextCropPage}
          cropSearchTerm={cropSearchTerm}
          onLoadMoreCrops={fetchNextCropPage}
          onCropChange={handleCropChange}
          onCropSearch={onCropSearch}
          onVarietyChange={handleVarietyChange}
          enableAddRecord
          onCropAdded={handleCropAdded}
          onVarietyAdded={handleVarietyAdded}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onZoneAreaChange={handleZoneAreaChange}
          onUnitChange={handleUnitChange}
        />
      )}
    </Modal>
  );
};

export default CreatePlanModal;
